"use client";

import { Bell, MoreVert, ProfileCircle } from "iconoir-react";
import { useLocale } from "next-intl";
import * as React from "react";

import { StatCounter } from "@/components/blocks/stat-counter";
import { LOCALE_CURRENCY } from "@/lib/locale-prefix";
import { cn } from "@/lib/utils";

const MOCK_WIDTH = 420;
const MOCK_HEIGHT = 270;
/** Back-stack card shadow extends ~48px past the 420px canvas (Figma 6632:29303). */
const MOCK_SHADOW_BLEED_RIGHT = 48;

/** Available funds count-up — Figma 10961:44137 (WEB-645). */
const AVAILABLE_FUNDS_START = 2_000_000;
const AVAILABLE_FUNDS_STOPS = [2_040_000, 2_080_000, 2_110_000];
const AVAILABLE_FUNDS_END = 2_120_500;
const AVAILABLE_FUNDS_DURATION_MS = 11_000;
const AVAILABLE_FUNDS_STOP_PAUSE_MS = 2_000;

const PANEL_SHADOW =
  "shadow-[244px_350px_120px_0px_rgba(0,0,0,0),156px_224px_109px_0px_rgba(0,0,0,0.01),88px_126px_92px_0px_rgba(0,0,0,0.02),39px_56px_68px_0px_rgba(0,0,0,0.02),10px_14px_38px_0px_rgba(0,0,0,0.03)]";

/** Back stack card — Figma 6632:29303 / 10961:44118 (stronger shadow than front panel). */
const BACK_STACK_SHADOW =
  "shadow-[244px_350px_120px_0px_rgba(0,0,0,0),156px_224px_109px_0px_rgba(0,0,0,0.01),88px_126px_92px_0px_rgba(0,0,0,0.02),39px_56px_68px_0px_rgba(0,0,0,0.06),10px_14px_38px_0px_rgba(0,0,0,0.05)]";

export interface AvailableFundsCardMockProps {
  /** Center header label inside the mock chrome (page-specific). */
  headerLabel?: string;
  /** Prefix for data-visual-id hooks (defaults to generic bento id). */
  visualIdPrefix?: string;
}

/**
 * Available funds mini-card mock — live HTML rebuild (Figma 6632:29307 desktop /
 * 9762:32454 mobile). Canonical animation from `/partners` "Expand Your Reach".
 * StatCounter counts up on scroll; no baked PNG text.
 */
export function AvailableFundsCardMockScaled({
  headerLabel = "Flexible funding",
  visualIdPrefix = "available-funds-card",
}: AvailableFundsCardMockProps) {
  const locale = useLocale();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(1);

  React.useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const updateScale = () => {
      setScale(Math.min(1, node.clientWidth / MOCK_WIDTH));
    };

    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className="w-full max-w-[420px] overflow-visible"
      style={{
        height: MOCK_HEIGHT * scale,
        marginRight: -(MOCK_SHADOW_BLEED_RIGHT * scale),
      }}
    >
      <div
        className="origin-top-left"
        style={{
          width: MOCK_WIDTH,
          height: MOCK_HEIGHT,
          transform: `scale(${scale})`,
        }}
      >
        <AvailableFundsCardMock
          locale={locale}
          headerLabel={headerLabel}
          visualIdPrefix={visualIdPrefix}
        />
      </div>
    </div>
  );
}

function AvailableFundsCardMock({
  locale,
  headerLabel,
  visualIdPrefix,
}: {
  locale: string;
  headerLabel: string;
  visualIdPrefix: string;
}) {
  return (
    <div
      aria-hidden
      className="relative h-[270px] w-[420px] max-w-none shrink-0 overflow-visible"
      data-visual-id={`${visualIdPrefix}-mock`}
    >
      {/* Back stack card — Figma 6632:29303 / 10961:44118 @ left 335 top 51 (85×219). */}
      <div
        className={cn(
          "border-grey-100 from-grey-50 absolute top-[51px] left-[335px] z-0 h-[219px] w-[85px] rounded-t-[24px] border-t border-r border-l bg-gradient-to-b to-white",
          BACK_STACK_SHADOW
        )}
        data-visual-id={`${visualIdPrefix}-back-stack`}
      />

      {/* Outer chrome — Figma 6632:29307 / 10961:44122 (392×270 front panel). */}
      <div
        className={cn(
          "border-grey-100 from-grey-50 absolute top-0 left-0 z-[1] h-full w-[392px] overflow-visible rounded-t-[24px] border-t border-r border-l bg-gradient-to-b to-white",
          PANEL_SHADOW
        )}
      />

      {/* Header icon shadows — Figma 10961:44146 / 44145 */}
      <div className="bg-grey-0 absolute top-[34px] left-[33px] z-[1] size-11 rounded-full shadow-[0px_4px_8px_rgba(24,37,75,0.08)]" />
      <div className="bg-grey-0 absolute top-[34px] left-[313px] z-[1] size-11 rounded-full shadow-[0px_4px_8px_rgba(24,37,75,0.08)]" />

      {/* Header icons — Figma 10961:44149 / 44150 */}
      <ProfileCircle
        className="text-grey-400 absolute top-[44px] left-[43px] z-[1] size-6"
        strokeWidth={1.5}
      />
      <div className="absolute top-[44px] left-[323px] z-[1] size-6">
        <Bell className="text-grey-400 size-6" strokeWidth={1.5} />
        <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full border-2 border-white bg-green-500" />
      </div>

      {/* Header label — Figma 10961:44147 (page-specific copy). */}
      <p className="text-grey-500 absolute top-[44px] left-[137px] z-[1] w-[116px] text-center text-[16px] leading-[1.5] tracking-[-0.02em]">
        {headerLabel}
      </p>

      {/* Gradient funds card — Figma 10961:44123 / 44124 */}
      <div className="absolute top-[104px] left-[33px] z-[1] h-[130px] w-[324px] overflow-hidden rounded-[16px]">
        <AvailableFundsGradientCard
          locale={locale}
          visualIdPrefix={visualIdPrefix}
        />
      </div>
    </div>
  );
}

function AvailableFundsGradientCard({
  locale,
  visualIdPrefix,
}: {
  locale: string;
  visualIdPrefix: string;
}) {
  return (
    <div
      className="bg-pulse-600 relative size-full overflow-hidden"
      data-visual-id={`${visualIdPrefix}-gradient`}
    >
      {/* Decorative gradient ellipses + grain — Figma 10961:44125–44133 */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute top-[-180px] left-[-715px] flex h-[1883px] w-[1903px] items-center justify-center">
          <div className="rotate-[30deg]">
            <div className="relative h-[1359px] w-[1412px]">
              <div className="absolute inset-[-9.05%_-8.7%]">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/assets-next/partners/bento/expand-reach-ellipse-1.svg"
                  alt=""
                  draggable={false}
                  className="block size-full max-w-none"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-[-104px] left-[-715px] flex h-[1883px] w-[1903px] items-center justify-center">
          <div className="rotate-[30deg]">
            <div className="relative h-[1359px] w-[1412px]">
              <div className="absolute inset-[-9.05%_-8.7%]">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/assets-next/partners/bento/expand-reach-ellipse-4.svg"
                  alt=""
                  draggable={false}
                  className="block size-full max-w-none"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-[-130px] left-[-715px] flex h-[1883px] w-[1903px] items-center justify-center">
          <div className="rotate-[30deg]">
            <div className="relative h-[1359px] w-[1412px]">
              <div className="absolute inset-[-9.05%_-8.7%]">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/assets-next/partners/bento/expand-reach-ellipse-4.svg"
                  alt=""
                  draggable={false}
                  className="block size-full max-w-none"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-[-100px] left-[-715px] flex h-[1883px] w-[1903px] items-center justify-center">
          <div className="rotate-[30deg]">
            <div className="relative h-[1359px] w-[1412px]">
              <div className="absolute inset-[-9.05%_-8.7%]">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src="/assets-next/partners/bento/expand-reach-ellipse-2.svg"
                  alt=""
                  draggable={false}
                  className="block size-full max-w-none"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-[-38px] left-[170px] h-[354px] w-[622px] mix-blend-overlay">
          <div className="absolute inset-[-34.77%_-19.75%]">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/assets-next/partners/bento/expand-reach-header-shadow.svg"
              alt=""
              draggable={false}
              className="block size-full max-w-none"
            />
          </div>
        </div>
        <div
          className="pointer-events-none absolute top-1/2 left-1/2 h-[266px] w-[472px] -translate-x-1/2 -translate-y-1/2 mix-blend-overlay"
          style={{
            backgroundImage:
              'url("/assets-next/partners/bento/expand-reach-grain-texture.png")',
            backgroundSize: "252px 252px",
            backgroundPosition: "top left",
          }}
        />
      </div>

      {/* Live content — Figma 10961:44136–44144 */}
      <div className="relative z-10 flex h-full flex-col px-6 pt-5 pb-4">
        <div className="flex flex-col text-white">
          <p className="text-[16px] leading-[1.5] tracking-[-0.02em]">
            Available funds
          </p>
          <p className="text-[32px] leading-[1.2] tracking-[-0.04em] tabular-nums">
            <StatCounter
              startValue={AVAILABLE_FUNDS_START}
              stops={AVAILABLE_FUNDS_STOPS}
              value={AVAILABLE_FUNDS_END}
              locale={locale}
              currency={LOCALE_CURRENCY[locale] ?? "USD"}
              durationMs={AVAILABLE_FUNDS_DURATION_MS}
              stopPauseMs={AVAILABLE_FUNDS_STOP_PAUSE_MS}
            />
          </p>
        </div>

        <div className="mt-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="bg-grey-0 text-grey-950 inline-flex h-6 items-center rounded-full px-3 text-[12px] leading-none font-medium tracking-[-0.02em] shadow-[0px_4px_1px_-2px_rgba(0,0,0,0.04)]">
              Send
            </span>
            <span className="border-grey-0/10 flex size-6 items-center justify-center rounded-full border-[1.5px] border-white bg-white/10 shadow-[0px_4px_1px_-2px_rgba(0,0,0,0.04)]">
              <MoreVert className="size-3.5 text-white" strokeWidth={1.5} />
            </span>
          </div>
          <p className="text-[14px] leading-[1.5] tracking-[-0.02em] text-white/50">
            June 2026
          </p>
        </div>
      </div>
    </div>
  );
}
